import { CdkDragDrop } from '@angular/cdk/drag-drop';
import {
  ChangeDetectionStrategy,
  Component,
  OnDestroy,
  OnInit,
  signal,
} from '@angular/core';
import isEqual from 'lodash/isEqual';
import { Subject } from 'rxjs';
import { catchError, finalize, takeUntil } from 'rxjs/operators';
import { DropdownService } from 'src/app/project/components/dropdown/dropdown-service/dropdown.service';
import { ResponseErrorService } from 'src/app/project/modules/errors/response-error.service';
import { PreferencesService } from 'src/app/project/modules/preferences/preferences-service/preferences.service';
import { TSavedView } from 'src/app/project/modules/saved-views/models/saved-view.model';
import { SavedViewsService } from 'src/app/project/modules/saved-views/saved-views.service';
import { PermissionsService } from 'src/app/project/modules/share/permissions.service';
import { TUser } from 'src/app/project/modules/user/user.model';
import { UserService } from 'src/app/project/modules/user/user.service';
import { checkCustomWorkspaceId } from 'src/app/project/modules/workspace/workspace';
import { TWorkspace, TWorkspacesById } from 'src/app/project/modules/workspace/workspace.model';
import { WorkspaceService } from 'src/app/project/modules/workspace/workspace.service';
import { ActiveService } from 'src/app/project/services/active/active.service';
import { logEventInGTAG } from 'src/app/project/services/analytics/google-analytics';
import {
  EGoogleEventCategory,
  EGoogleEventSorting,
} from 'src/app/project/services/analytics/google-analytics.consts';
import { GoogleAnalyticsService } from 'src/app/project/services/analytics/google-analytics.service';
import { iconMap } from 'src/app/project/shared/enums/icon-map';
import { EIconPath } from 'src/app/project/shared/enums/icons.enum';
import { ResetTableService } from '../../../reset-table.service';
import { TColumn } from '../../../site-table/columns/column.model';
import { sortingSignal } from '../../../site-table/columns/sorting.store';
import { TableColumnsService } from '../../../site-table/columns/table-columns.service';
import { CustomTableService } from '../../../site-table/custom-table/custom-table.service';
import {
  SiteTableColumnsDataService,
  TColumnsData,
} from '../../../site-table/site-table-columns-data.service';
import { ESortOrder } from '../../../site-table/sort-order-enum';
import { SortButtonDropdownService } from './sort-button-dropdown.service';

type TSortingSnapshot = Pick<TColumn, 'name' | 'sortIndex' | 'sortOrder'>;

@Component({
  selector: 'pp-sort-button-dropdown',
  templateUrl: './sort-button-dropdown.component.html',
  styleUrls: ['./sort-button-dropdown.component.scss'],
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SortButtonDropdownComponent implements OnInit, OnDestroy {
  columns: TColumnsData = this.siteTableColumnsDataService.getColumns();
  private readonly destroy$ = new Subject<void>();

  public tagsVisible: boolean;
  public user: TUser;
  workspace: TWorkspace;
  workspaces: TWorkspacesById;
  siteId = '';
  EIconPath = EIconPath;
  iconMap = iconMap;

  readonly sorting = sortingSignal;
  readonly emptySortsVisible = signal<number[]>([]);
  readonly saveViewProcessing = signal(false);
  readonly scrollPosition = signal(0);
  readonly scrolledToBottom = signal(false);

  isDropdownVisible: boolean;
  activeViewName = '';
  activeView: TSavedView;

  private cachedSorting: TSortingSnapshot[] = [];
  private scrollRaf: number | null = null;

  constructor(
    private dropdownService: DropdownService,
    private siteTableColumnsDataService: SiteTableColumnsDataService,
    private workspaceService: WorkspaceService,
    private activeService: ActiveService,
    private googleAnalyticsService: GoogleAnalyticsService,
    private userService: UserService,
    private permissionsService: PermissionsService,
    private preferencesService: PreferencesService,
    private sortButtonDropdownService: SortButtonDropdownService,
    private customTableService: CustomTableService,
    private savedViewsService: SavedViewsService,
    private responseErrorService: ResponseErrorService,
    private tableColumnsService: TableColumnsService,
    private resetTableService: ResetTableService,
  ) {}

  ngOnInit() {
    this.setData();

    this.checkSavedViews();
  }

  ngOnDestroy(): void {
    const currentSnapshot = this.sorting().map((col) => ({
      name: col.name,
      sortIndex: col.sortIndex,
      sortOrder: col.sortOrder,
    }));

    if (!isEqual(this.cachedSorting, currentSnapshot)) {
      this.customTableService.reloadTable();
    }

    if (this.scrollRaf !== null) {
      cancelAnimationFrame(this.scrollRaf);
      this.scrollRaf = null;
    }

    this.destroy$.next();
    this.destroy$.complete();
  }

  clearAll(): void {
    this.resetTableService.resetSorting();
    this.hideDropdown();
  }

  dropSorting(event: CdkDragDrop<TColumn[]>): void {
    if (event.previousIndex === event.currentIndex) {
      return;
    }

    const column = event.item.data as TColumn;
    this.sortButtonDropdownService.moveSort(column, event.currentIndex);

    const workspaceId = checkCustomWorkspaceId() || this.activeService.getActiveWorkspaceId();
    this.preferencesService.savePreferences(workspaceId);
  }

  scrollHandler(event: Event): void {
    if (!(event.target instanceof HTMLElement)) {
      return;
    }

    const target = event.target;

    if (this.scrollRaf !== null) {
      cancelAnimationFrame(this.scrollRaf);
    }

    this.scrollRaf = requestAnimationFrame(() => {
      this.scrollPosition.set(target.scrollTop);
      this.scrolledToBottom.set(
        target.scrollHeight - target.scrollTop === target.clientHeight,
      );
      this.scrollRaf = null;
    });
  }

  hideDropdown(): void {
    this.dropdownService.hideDropdown();
  }

  deleteSort(column: TColumn): void {
    this.sortButtonDropdownService.removeSort(column);

    logEventInGTAG(EGoogleEventSorting.DELETE_SORTING, {
      source: EGoogleEventCategory.SORTING,
      type: this.googleAnalyticsService.getColumnType(column),
    });
  }

  addEmptySort(): void {
    this.emptySortsVisible.update((arr) => [...arr, 1]);
  }

  deleteEmptySort(): void {
    this.emptySortsVisible.update((arr) => arr.slice(0, -1));
  }

  updateEmptySortColumn(column: TColumn): void {
    this.sortButtonDropdownService.addSort(column, ESortOrder.ASC);

    this.emptySortsVisible.update((arr) => arr.slice(0, -1));

    logEventInGTAG(EGoogleEventSorting.ADD_SORTING, {
      source: EGoogleEventCategory.SORTING,
      type: this.googleAnalyticsService.getColumnType(column),
    });
  }

  saveSortsToView(): void {
    this.activeView.columns = this.updateViewColumnsSorting();

    this.saveViewProcessing.set(true);

    this.savedViewsService
      .saveToView(this.activeView, checkCustomWorkspaceId() || this.workspace?.workspaceId)
      .pipe(
        takeUntil(this.destroy$),
        catchError((error) =>
          this.responseErrorService.handleRequestError(error, 'Failed to save sorting to view'),
        ),
        finalize(() => this.saveViewProcessing.set(false)),
      )
      .subscribe();
  }

  updateOrder(item: ESortOrder, column: TColumn): void {
    this.sortButtonDropdownService.changeSortOrder(item, column);

    logEventInGTAG(EGoogleEventSorting.UPDATE_SORTING, {
      source: EGoogleEventCategory.SORTING,
      type: this.googleAnalyticsService.getColumnType(column),
    });
  }

  updateColumn(column: TColumn, oldColumn: TColumn): void {
    this.sortButtonDropdownService.replaceSort(column, oldColumn);

    logEventInGTAG(EGoogleEventSorting.UPDATE_SORTING, {
      source: EGoogleEventCategory.SORTING,
      type: this.googleAnalyticsService.getColumnType(column),
    });
  }

  private updateViewColumnsSorting(): TColumn[] {
    // We need to only update sorting and leave grouping, position, width and hidden as is
    const columns = this.tableColumnsService.getColumns();

    return this.activeView.columns.map((column) => {
      // If we enable saved views for overview we'll also need to check custom field ids because name won't be enough
      const matchingColumn = columns.find((c) => c.name === column.name);

      if (matchingColumn) {
        column.sortIndex = matchingColumn.sortIndex;
        column.sortOrder = matchingColumn.sortOrder;
      } else {
        column.sortIndex = null;
        column.sortOrder = null;
      }

      return column;
    });
  }

  private setData(): void {
    this.cachedSorting = this.sorting().map((col) => ({
      name: col.name,
      sortIndex: col.sortIndex,
      sortOrder: col.sortOrder,
    }));

    this.workspace = this.workspaceService.getActiveWorkspace();
    this.workspaces = this.workspaceService.getWorkspaces();
    this.user = this.userService.getUser();

    if (this.workspace) {
      const tagPermissions = this.permissionsService.getTagPermissions(this.workspace?.workspaceId);
      this.siteId = checkCustomWorkspaceId() || this.workspace?.workspaceId;
      this.tagsVisible = tagPermissions.read;
    } else {
      this.columns = this.siteTableColumnsDataService.getAllColumns();
      this.tagsVisible = true;
      this.siteId = checkCustomWorkspaceId();
    }
  }

  private checkSavedViews(): void {
    const activeViewId = this.savedViewsService.getSelectedViewId(this.siteId);
    this.activeView = this.savedViewsService.getView(activeViewId);
    this.activeViewName = this.activeView?.name;
  }
}
